/**
 * Résolution de l'URL du style MapLibre servi par le proxy MapTiler du backend.
 *
 * Le backend expose `/api/v1/map/style/<id>/` qui proxifie MapTiler (clé cachée
 * côté serveur) et retombe sur un fond raster Carto si non configuré. On ne fait
 * donc jamais référence à MapTiler directement depuis le frontend.
 */
import { getApiBaseUrl, getApiVersion } from '../../../shared/api/runtimeConfig'
import i18n from '../../../i18n'

/** Fonds de carte proposés dans « Options de carte » (carte principale). */
export type MapBackgroundType =
  | 'flat'
  | 'topo'
  | 'rando'
  | 'velo'
  | 'riviere'
  | 'satellite'

/** Identifiant de style par défaut pour les cartes secondaires (flat clair). */
export const DEFAULT_MAP_STYLE_ID = 'pmtiles-flat-light'

/** Mapping fond UI → identifiant de style (proxifié). */
export const MAP_STYLE_IDS: Record<MapBackgroundType, string> = {
  flat: 'pmtiles-flat-light',
  topo: 'topo-light',
  rando: 'topo-light',
  velo: 'pmtiles-flat-light',
  riviere: 'pmtiles-flat-light',
  satellite: 'raster-satellite',
}

/** Libellés et descriptions affichés dans le panneau « Options de carte ». */
export const MAP_BACKGROUND_OPTIONS: {
  id: MapBackgroundType
  name: string
  description: string
}[] = [
  {
    id: 'flat',
    get name() { return i18n.t('map:mapStyle.fond_plat') },
    get description() { return i18n.t('map:mapStyle.carte_claire_sans_relief_ni') },
  },
  {
    id: 'topo',
    get name() { return i18n.t('map:mapStyle.fond_topo') },
    get description() { return i18n.t('map:mapStyle.relief_courbes_niveau_ombrage_terrain') },
  },
  {
    id: 'rando',
    get name() { return i18n.t('map:mapStyle.fond_rando') },
    get description() { return i18n.t('map:mapStyle.sentiers_randonnee_accentues_traits_epai') },
  },
  {
    id: 'velo',
    get name() { return i18n.t('map:mapStyle.fond_velo') },
    get description() { return i18n.t('map:mapStyle.pistes_cyclables_routes_avec_amenagement') },
  },
  {
    id: 'riviere',
    get name() { return i18n.t('map:mapStyle.fond_riviere') },
    get description() { return i18n.t('map:mapStyle.cours_eau_lacs_milieux_naturels') },
  },
  {
    id: 'satellite',
    get name() { return i18n.t('map:mapStyle.fond_satellite') },
    get description() { return i18n.t('map:mapStyle.vue_satellite_haute_resolution') },
  },
]

/** Valeur par défaut du fond de carte (carte principale). */
export const DEFAULT_MAP_BACKGROUND: MapBackgroundType = 'rando'

const LEGACY_BACKGROUND_MAP: Record<string, MapBackgroundType> = {
  terrain: 'rando',
  topographic: 'topo',
  satellite: 'satellite',
}

/** Restaure une valeur persistée (localStorage) en gérant les anciennes clés. */
export function parseStoredMapBackground(stored: string | null): MapBackgroundType | null {
  if (!stored) return null
  if (stored in MAP_STYLE_IDS) return stored as MapBackgroundType
  return LEGACY_BACKGROUND_MAP[stored] ?? null
}

/** Centre par défaut (Québec) — utilisé par les pickers/cartes sans valeur. */
export const DEFAULT_MAP_CENTER = { longitude: -72.5793, latitude: 46.3432 }

/**
 * Tuiles PMTiles du fond (Protomaps v4), même URL que les styles
 * `pmtiles_styles/*.json` du backend (pipelines/fonds-de-carte).
 */
export const BASEMAP_PMTILES_URL =
  'pmtiles://https://s3.us-east-005.backblazeb2.com/OutdoorPlanning/maps/quebec-east.pmtiles'

/**
 * Emprise des tuiles PMTiles régionales (quebec-east.pmtiles), avec petite marge.
 * Aligné sur l'extract : --bbox="-85.0,41.5,-52.5,62.5"
 * (Ontario, Québec, Maritimes, Terre-Neuve, nord-est USA).
 */
export const MAP_COVERAGE_BOUNDS = {
  west: -85.1,
  south: 41.4,
  east: -52.4,
  north: 62.6,
} as const

/**
 * Marge à l'est de Terre-Neuve pour le pan carte (océan visible à droite de l'île).
 * ~1,8° ≈ 130–150 km à la latitude de Terre-Neuve (1° lng ≈ 74–80 km).
 */
const MAP_VIEW_EAST_PADDING_DEG = 1.8

/** Limite de pan MapLibre : [[ouest, sud], [est, nord]]. */
export const MAP_MAX_BOUNDS: [[number, number], [number, number]] = [
  [MAP_COVERAGE_BOUNDS.west, MAP_COVERAGE_BOUNDS.south],
  [MAP_COVERAGE_BOUNDS.east + MAP_VIEW_EAST_PADDING_DEG, MAP_COVERAGE_BOUNDS.north],
]

/** Zoom minimum pour éviter de dézoomer hors de la zone couverte. */
export const MAP_MIN_ZOOM = 4

/** Construit l'URL du style proxy pour un identifiant MapTiler donné. */
export function getMapStyleUrl(styleId: string = DEFAULT_MAP_STYLE_ID): string {
  return `${getApiBaseUrl()}/${getApiVersion()}/map/style/${styleId}/`
}

/** URL du style pour un fond de carte UI. */
export function getBackgroundStyleUrl(background: MapBackgroundType): string {
  return getMapStyleUrl(MAP_STYLE_IDS[background] ?? DEFAULT_MAP_STYLE_ID)
}

/** URL du style par défaut (cartes secondaires : pickers, fiches, planning). */
export function getDefaultMapStyleUrl(): string {
  return getMapStyleUrl(DEFAULT_MAP_STYLE_ID)
}
